import { Panel, PanelHeader } from '@/components/ui/display'
import { cn } from '@/lib/utils'

/**
 * One settings group.
 *
 * `PanelHeader` has no description slot, and this does not add one back. The
 * caption goes in `meta` — right-aligned, small, one line — and anything that
 * needs a sentence to explain it gets that sentence on the field itself, where
 * the reader is when the question comes up. A paragraph under every heading is
 * how the old settings page ended up three screens long.
 */
export function SettingsCard({
  title,
  meta,
  actions,
  children,
  className,
}: {
  title: React.ReactNode
  meta?: React.ReactNode
  actions?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <Panel className={cn('mt-3', className)}>
      <PanelHeader title={title} meta={meta} actions={actions} />
      <div className="space-y-4 px-3 py-3">{children}</div>
    </Panel>
  )
}

/**
 * Switch rows, ruled between. Same container the template editor uses, so a
 * boolean looks the same wherever it is asked for.
 */
export function SwitchGroup({ children }: { children: React.ReactNode }) {
  return (
    <div className="divide-y divide-[var(--color-border-subtle)] rounded-[var(--radius-md)] border border-[var(--color-border)] px-3">
      {children}
    </div>
  )
}

/**
 * The consequence of the setting above, in the app's own words.
 *
 * Not a hint — a hint says what a control does, this says what is true now.
 * It changes as the control changes, which is what makes it worth its space.
 */
export function Consequence({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-sunken)] px-3 py-2 text-xs leading-relaxed text-[var(--color-fg-muted)]">
      {children}
    </p>
  )
}
